import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, H1, H2, H3, H4, Body, BodySm, Text } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { cn } from '@/lib/utils';
import { DEMO_USER, useDemoAuth } from '@/lib/demo-auth';

export function PricingPage() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const { user: demoUser, logoutDemo } = useDemoAuth();

  const activeUser = isDemo ? demoUser : null;
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const pricingTiers = [
    {
      name: 'Free',
      description: 'For legal entertainment enthusiasts',
      monthlyPrice: 0,
      yearlyPrice: 0,
      tokens: 500,
      features: [
        '500 one-time bonus tokens',
        'Basic personality',
        'Standard disclaimers',
        'Up to 2 documents',
        'Community support',
      ],
    },
    {
      name: 'Basic',
      description: 'For casual questionable advice seekers',
      monthlyPrice: 10,
      yearlyPrice: 100,
      tokens: 10000,
      features: [
        '10,000 tokens/month',
        'Enhanced personality',
        'Extra disclaimers',
        'Up to 10 documents',
        'Priority responses',
        'Email support',
      ],
      popular: true,
    },
    {
      name: 'Pro',
      description: 'For serious entertainment lawyers',
      monthlyPrice: 29,
      yearlyPrice: 290,
      tokens: 35000,
      features: [
        '35,000 tokens/month',
        'Maximum sass mode',
        'Warrior-level disclaimers',
        'Unlimited documents',
        'Faster streaming',
        'Priority support',
        'Early access to features',
      ],
    },
    {
      name: 'Enterprise',
      description: 'For companies that love liability',
      monthlyPrice: 99,
      yearlyPrice: 990,
      tokens: 150000,
      features: [
        '150,000 tokens/month',
        'Dedicated account manager',
        'Custom personality tuning',
        'API access',
        'SLA guarantee',
        'White-label options',
        'Everything in Pro',
      ],
    },
  ];

  const tokenPacks = [
    { name: 'Starter Pack', tokens: 2500, price: 5 },
    { name: 'Regular Pack', tokens: 10000, price: 15 },
    { name: 'Power Pack', tokens: 50000, price: 60 },
  ];

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <header className="bg-white border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <NotLegalIcon name="zap" className="w-8 h-8 text-primary-600" />
            <span className="text-xl font-bold text-surface-900">notlegal.chat</span>
            {isDemo && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                Demo
              </span>
            )}
          </Link>
          <div className="flex items-center space-x-3">
            {isDemo ? (
              <Link to="/" onClick={logoutDemo} className="flex items-center space-x-2 text-surface-500 hover:text-red-600 transition-colors">
                <NotLegalIcon name="logout" className="w-4 h-4" />
                <span>Exit Demo</span>
              </Link>
            ) : (
              <>
                <Link to="/sign-in">
                  <Button variant="ghost" size="sm">Sign In</Button>
                </Link>
                <Link to="/sign-up">
                  <Button size="sm">Get Started</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Demo Mode Notice */}
          {isDemo && (
            <div className="mb-8 bg-amber-50 border border-amber-200 rounded-card p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <NotLegalIcon name="user" className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <H4 className="text-amber-800">Demo Mode Active</H4>
                  <Body className="text-amber-700 text-sm">
                    You're viewing demo mode. Your current plan: <strong>{DEMO_USER.subscriptionTier}</strong> with <strong>{DEMO_USER.tokenBalance.toLocaleString()}</strong> tokens.
                  </Body>
                </div>
              </div>
              <Link to="/">
                <Button variant="ghost" size="sm">Exit Demo</Button>
              </Link>
            </div>
          )}

          {/* Pricing Header */}
          <div className="text-center mb-16">
            <H1>Pricing That Won't Break the Bank</H1>
            <Body className="mt-4 text-lg text-surface-600 max-w-2xl mx-auto">
              Pay for tokens, not hourly rates. Cancel anytime.
              No lawyers were consulted in the making of this pricing model.
            </Body>

            {/* Billing Toggle */}
            <div className="mt-8 flex items-center justify-center space-x-4">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={cn(
                  'px-6 py-2 rounded-full text-sm font-medium transition-all',
                  billingCycle === 'monthly'
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                )}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={cn(
                  'px-6 py-2 rounded-full text-sm font-medium transition-all',
                  billingCycle === 'yearly'
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                )}
              >
                Yearly
                <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                  Save 17%
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {pricingTiers.map((tier, index) => (
              <Card
                key={index}
                variant={tier.popular ? 'elevated' : 'default'}
                className={cn(
                  'relative',
                  tier.popular ? 'border-2 border-primary-500' : ''
                )}
                padding="lg"
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <H3 className="text-center">{tier.name}</H3>
                <p className="text-sm text-surface-500 text-center mt-1">{tier.description}</p>
                <div className="text-center mt-6">
                  <span className="text-4xl font-bold text-surface-900">
                    ${billingCycle === 'monthly' ? tier.monthlyPrice : Math.round(tier.yearlyPrice / 12)}
                  </span>
                  <span className="text-surface-500">/mo</span>
                </div>
                <p className="text-center text-primary-600 font-medium mt-2">
                  {tier.tokens.toLocaleString()} tokens/mo
                </p>
                <ul className="mt-6 space-y-3">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start text-sm text-surface-600">
                      <NotLegalIcon name="shield" className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to={isDemo ? '/chat?demo=true' : '/sign-up'} className="block mt-6">
                  <Button
                    variant={tier.popular ? 'primary' : 'secondary'}
                    className="w-full"
                  >
                    {isDemo ? 'Current Plan' : 'Get Started'}
                  </Button>
                </Link>
              </Card>
            ))}
          </div>

          {/* Token Packs */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <H2>Need More Tokens?</H2>
              <Body className="mt-4 text-surface-600">
                Buy token packs anytime. No expiration. No subscription required.
              </Body>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {tokenPacks.map((pack, index) => (
                <Card key={index} padding="lg">
                  <div className="text-center">
                    <NotLegalIcon name="gift" className="w-10 h-10 text-primary-600 mx-auto mb-4" />
                    <H4>{pack.name}</H4>
                    <div className="mt-4">
                      <span className="text-3xl font-bold text-surface-900">{pack.tokens.toLocaleString()}</span>
                      <span className="text-surface-500"> tokens</span>
                    </div>
                    <div className="mt-2">
                      <span className="text-xl font-semibold text-primary-600">${pack.price}</span>
                    </div>
                    <Button variant="secondary" className="w-full mt-6">
                      {isDemo ? 'Purchase (Demo)' : 'Purchase'}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto mt-20">
            <H2 className="text-center mb-12">Frequently Asked Questions</H2>
            <div className="space-y-6">
              <Card>
                <H4>What are tokens?</H4>
                <Body className="mt-2 text-surface-600">
                  Tokens are our fake currency that power your conversations. More tokens = more questions = more disclaimers. Approximately 750 words = 1,000 tokens.
                </Body>
              </Card>
              <Card>
                <H4>Can I get a refund?</H4>
                <Body className="mt-2 text-surface-600">
                  Probably not, but you can ask our support team. We make no promises. Tokens have no cash value. They're basically digital stickers.
                </Body>
              </Card>
              <Card>
                <H4>Is this actually legal advice?</H4>
                <Body className="mt-2 text-surface-600">
                  Absolutely not! We cannot stress this enough. This is entertainment only. Please consult a licensed attorney for actual legal matters.
                </Body>
              </Card>
              <Card>
                <H4>What happens if I run out of tokens?</H4>
                <Body className="mt-2 text-surface-600">
                  You can upgrade your plan, buy token packs, or just stop using the service. We won't miss you. (That's a joke. We'd totally miss you.)
                </Body>
              </Card>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="max-w-3xl mx-auto mt-16 text-center">
            <div className="bg-warning-50 border border-warning-200 rounded-card p-6">
              <NotLegalIcon name="shield" className="w-8 h-8 text-warning-600 mx-auto mb-4" />
              <H4>Legal Disclaimer</H4>
              <Body className="mt-2 text-surface-600">
                Prices and features may change at any time. We make no guarantees.
                By using this service, you agree to our Terms of Service and acknowledge that we provide <span className="italic">not legal advice</span>.
              </Body>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
